import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, Inject } from '@angular/core';
import { FormGroup, FormControl, FormArray, Validators, FormBuilder } from '@angular/forms';
import { State, BhtpJurisdictionsService } from '@bhtp-client-api/eligibility';
import { tripCostValidator, depositDateValidator, dateOfBirthValidator, departureDateValidator, returnDateValidator, emailValidator, EmailViewModel } from '@bhtp-client-web/component';
import { FeatureService, PurchaseData, Flight, Traveler, Address } from './../shared';
import { IApiAddress, ApiAddress } from '@bhtp-client-api/base';

@Component({
    selector: 'quick-quote-entry',
    template: require('./quick-quote-entry.component.html')
})
export class QuickQuoteEntryComponent implements OnInit, OnChanges {
    @Input() quote: PurchaseData = new PurchaseData();
    @Input() maxTravelers: number = 25;
    @Input() showFlightSegments: boolean = true;
    @Input() maxDate: string;
    @Input() states: State[];
    @Input() isAmbassador: boolean;
    @Input() currentAgentCode: string;
    @Input() addressResult: IApiAddress;
    @Output() validQuote: EventEmitter<PurchaseData> = new EventEmitter<PurchaseData>();
    @Output() temporaryQuoteChanged: EventEmitter<PurchaseData> = new EventEmitter<PurchaseData>();
    @Output() valuesChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() postalCodeChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() agentCodeChanged: EventEmitter<string> = new EventEmitter<string>();

    public tripForm: FormGroup;
    public postalCodeControl: FormControl;
    public departureDateControl: FormControl;
    public returnDateControl: FormControl;
    public initialDepositDateControl: FormControl;
    public dateOfBirthControl: FormControl;
    public tripCostControl: FormControl;
    public destinationCountryControl: FormControl;
    public flightSegmentsControl: FormControl;
    public additionalTravelersArray: FormArray;
    public agentCodeControl: FormControl;
    public emailGroupControl: FormGroup;

    public errorMessages: any = {};

    public submitted: boolean = false;

    public flightSegments: Array<number> = new Array<number>();

    private temporaryQuote: PurchaseData;
    private defaultFlightSegments: number = null;
    private disableAgentCode: boolean = true;
    private readonly agentCodeCharacterLength: number = 6;
    private initialized: boolean = false;
    private cityAndState: string;

    private validationMessages: any = {
        'departureDateControl': {
            'required': 'Departure Date is required.',
            'invalid': 'Departure Date must be a valid date in mm/dd/yyyy format.',
            'afterMax': 'Departure Date must be on or before ' + this.maxDate + '.',
            'beforeToday': 'Departure Date must be after today.'
        },
        'returnDateControl': {
            'required': 'Return Date is required.',
            'invalid': 'Return Date must be a valid date in mm/dd/yyyy format.',
            'beforeDeparture': 'Return Date must be on or after Departure Date.',
            'afterMax': 'Return Date must be on or before ' + this.maxDate + '.',
            'beforeToday': 'Return Date must be after today.'
        },
        'initialDepositDateControl': {
            'required': 'Deposit Date is required.',
            'invalid': 'Deposit Date must be a valid date in mm/dd/yyyy format.',
            'afterToday': 'Deposit Date must be today or in the past.',
        },
        'dateOfBirthControl': {
            'required': 'Date of Birth is required.',
            'invalid': 'Date of Birth must be a valid date in mm/dd/yyyy format.',
            'afterToday': 'Date of Birth cannot be in the future.',
        },
        'tripCostControl': {
            'required': 'Trip Cost is required.',
            'invalid': 'Please enter a valid number.',
            'min': 'Trip Cost cannot be less than $0.',
            'max': 'Trip Cost should be less than $100,000 per traveler.',
        },
        'destinationCountryControl': {
            'required': 'Destination Country is required.'
        },
        'flightSegmentsControl': {
            'required': 'Number of Flight Segments is required.',
        },
        'agentCodeControl': {
            'maxLength': 'Agent Codes are limited to ' + this.agentCodeCharacterLength + ' characters in length',
            'pattern' : 'Agent Codes are limited to 2 alpha characters followed by for numbers ex. AA0000.'
        },
        'emailGroup': {
            'required': 'Primary Traveler\'s Email Address is required.',
            'invalid': 'Please enter a valid email address.',
            'maxlength': 'Email address cannot be more than 80 characters long.'
        },
        'postalCodeControl': {
            'required': 'Postal Code of primary traveler is required.',
            'notFound': 'We didn’t recognize this Postal Code. Please verify the postal code is correct or call us at 844-411-BHTP',
            'pattern': 'Postal Code must only contain numbers.',
            'maxlength': 'Postal Code must be a 5 digit number.'
        }
    };

    constructor(private featureService: FeatureService, private formBuilder: FormBuilder, @Inject('portalService') private portalService: any) { }

    public ngOnInit(): void {
        this.defaultFlightSegments = this.featureService.getDefaultFlightSegments();
        for (let i = 0; i <= 10; i++) {
            this.flightSegments.push(i);
        }

        this.setupValidationAndControls();

        this.portalService.loadStates().then((response) => {
            this.states = response;
            this.initialized = true;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.addressResult && this.initialized) {
            this.processAddressChange(changes);
        }
    }

    public onPostalCodeEntered(newPostalCode: string): void {
        if (newPostalCode) {
            this.disableAgentCode = false;
            this.postalCodeChanged.emit(newPostalCode);
        }

        this.postalCodeControl.markAsTouched();
    }

    public onAgentCodeEntered(newAgentCode: string): void {
        if (this.agentCodeControl.valid) {
            this.agentCodeChanged.emit(newAgentCode);
        }
        else {
            this.agentCodeControl.markAsTouched();
            this.agentCodeControl.updateValueAndValidity();
        }
    }

    public clearAgentCode(): void {
        if (this.agentCodeControl && this.agentCodeControl.value) {
            this.agentCodeControl.setValue('');
            this.agentCodeControl.updateValueAndValidity();
        }
    }

    private clearNotFoundError() {
        if (this.postalCodeControl && this.postalCodeControl.errors && this.postalCodeControl.errors.notFound) {
            delete this.postalCodeControl.errors.notFound;
        }
    }

    private processAddressChange(changes: SimpleChanges) {
        if (changes.addressResult.currentValue.error) {
            if (this.postalCodeControl.valid) {
                const errors: any = this.postalCodeControl.errors || {};
                errors.notFound = true;
                this.postalCodeControl.setErrors(errors);
            }
        } else {
            this.clearNotFoundError();
            this.writeFormValues(this.temporaryQuote);
            this.setCityStateForView();
            this.temporaryQuoteChanged.emit(this.temporaryQuote);
        }
    }

    private setCityStateForView() {
        try {
            this.cityAndState = `${this.addressResult.city},
            ${this.states.find(s => s.isoCode2 === this.addressResult.stateOrProvince).name}`;
        } catch (err) {
            // We don't want to stop the progress if there is an issue
        }
    }

    private addTraveler(): void {
        if (this.additionalTravelersArray.controls.length < this.maxTravelers) {
            this.additionalTravelersArray.push(this.initAdditionalTravelerForm(new Traveler(false)));
        }
    }

    private onRemoveTraveler(index: number): void {
        this.additionalTravelersArray.removeAt(index);
    }

    private onSubmit(): void {
        this.submitted = true;
        if (this.tripForm.valid) {
            this.writeFormValues(this.quote);
            this.validQuote.emit(this.quote);
        }
    }

    private writeFormValues(currentQuote: PurchaseData): void {
        currentQuote.departureDate = this.departureDateControl.value;
        currentQuote.returnDate = this.returnDateControl.value;
        currentQuote.depositDate = this.initialDepositDateControl.value;
        currentQuote.destinationCountry = this.destinationCountryControl.value;
        currentQuote.postalCode = this.postalCodeControl.value;

        if (this.showFlightSegments) {
            currentQuote.flights = new Array<Flight>();
            let numberOfFlights: number = this.flightSegmentsControl.value;
            for (let i = 0; i < numberOfFlights; i++) {
                currentQuote.flights.push(new Flight());
            }
        }

        if (this.tripCostControl.value || this.tripCostControl.value === 0) {
            currentQuote.primaryTraveler.tripCost = Number(this.tripCostControl.value);
        }

        currentQuote.primaryTraveler.emailGroup = this.emailGroupControl.value;

        currentQuote.primaryTraveler.birthDate = this.dateOfBirthControl.value;
        if (!currentQuote.primaryTraveler.address) {
            currentQuote.primaryTraveler.address = new Address();
        }

        currentQuote.primaryTraveler.address = this.addressResult;

        currentQuote.additionalTravelers = [];
        this.additionalTravelersArray.controls.forEach(fg => {
            let travelerGroup: FormGroup = <FormGroup>fg;
            let additionalTraveler = new Traveler(false);

            if (travelerGroup.controls['tripCostControl'].value || travelerGroup.controls['tripCostControl'].value === 0) {
                additionalTraveler.tripCost = Number(travelerGroup.controls['tripCostControl'].value);
            }

            additionalTraveler.birthDate = travelerGroup.controls['dateOfBirthControl'].value;
            currentQuote.additionalTravelers.push(additionalTraveler);
        });
    }

    private setupValidationAndControls(): void {
        if (!this.quote) {
            this.quote = new PurchaseData();
        }

        if (this.quote.residenceState) {
            this.disableAgentCode = false;
        }

        this.temporaryQuote = JSON.parse(JSON.stringify(this.quote));

        let formGroup: FormGroup = new FormGroup({});

        this.postalCodeControl = new FormControl(this.quote.postalCode || '', [Validators.required, Validators.maxLength(5), Validators.pattern('$|[0-9]')]);
        formGroup.addControl('postalCodeControl', this.postalCodeControl);

        this.departureDateControl = new FormControl(this.quote.departureDate || '', [Validators.required, departureDateValidator()]);
        formGroup.addControl('departureDateControl', this.departureDateControl);

        this.returnDateControl = new FormControl(this.quote.returnDate, [Validators.required, returnDateValidator(this.departureDateControl)]);
        formGroup.addControl('returnDateControl', this.returnDateControl);

        this.initialDepositDateControl = new FormControl(this.quote.depositDate || '', Validators.compose([Validators.required, depositDateValidator()]));
        formGroup.addControl('initialDepositDateControl', this.initialDepositDateControl);

        this.dateOfBirthControl = new FormControl(this.quote.primaryTraveler.birthDate || '', Validators.compose([Validators.required, dateOfBirthValidator()]));
        formGroup.addControl('dateOfBirthControl', this.dateOfBirthControl);

        this.tripCostControl = new FormControl(this.quote.primaryTraveler.tripCost, Validators.compose([Validators.required, tripCostValidator(0, 100000)]));
        formGroup.addControl('tripCostControl', this.tripCostControl);

        this.destinationCountryControl = new FormControl(this.quote.destinationCountry && this.quote.destinationCountry.name ? this.quote.destinationCountry : '', [Validators.required]);
        formGroup.addControl('destinationCountryControl', this.destinationCountryControl);

        if (this.showFlightSegments) {
            let selectedSegements: number = this.quote.flights ? this.quote.flights.length : this.defaultFlightSegments;
            this.flightSegmentsControl = new FormControl(Number.isNaN(selectedSegements) ? '' : selectedSegements, [Validators.required]);
            formGroup.addControl('flightSegmentsControl', this.flightSegmentsControl);
        }

        this.emailGroupControl = this.formBuilder.group({
            email: [this.quote.primaryTraveler.emailGroup.email, []],
            noEmail: [this.quote.primaryTraveler.emailGroup.noEmail, []],
        }, {
            validator: emailValidator(true, 80)
        });

        // when one control receives input, clear input on other control
        this.emailGroupControl.controls['email'].valueChanges.subscribe(data => {
            if (data && data.length > 0 && this.emailGroupControl.controls['noEmail'].value === true) {
                this.emailGroupControl.controls['noEmail'].setValue(false);
            }
        });

        this.emailGroupControl.controls['noEmail'].valueChanges.subscribe(data => {
            if (data === true) {
                this.emailGroupControl.controls['email'].setValue('');
                this.emailGroupControl.controls['email'].updateValueAndValidity();
            }
        });

        formGroup.addControl('emailGroup', this.emailGroupControl);

        let additionalTravelerForms: FormArray = new FormArray([]);
        if (this.quote.additionalTravelers) {
            this.quote.additionalTravelers.forEach(t => {
                additionalTravelerForms.push(this.initAdditionalTravelerForm(t));
            });
        }

        this.additionalTravelersArray = additionalTravelerForms;
        formGroup.addControl('additionalTravelersArray', this.additionalTravelersArray);

        if (this.isAmbassador) {
            this.agentCodeControl = new FormControl(this.currentAgentCode, Validators.compose([Validators.maxLength(this.agentCodeCharacterLength), Validators.pattern('$|[a-zA-Z]{2}\\d{4}')]));
            formGroup.addControl('agentCodeControl', this.agentCodeControl);

            this.agentCodeControl.valueChanges.debounceTime(500).subscribe(newAgentCode => {
                this.onAgentCodeEntered(newAgentCode);
            });
        }

        this.tripForm = formGroup;

        this.departureDateControl.valueChanges.subscribe(newValue => {
            // when the value of the departure date control changes, run
            // validation on return date control
            this.returnDateControl.updateValueAndValidity();
        });

        this.postalCodeControl.valueChanges.debounceTime(500).subscribe(newPostalCode => {
            this.cityAndState = undefined;
            this.postalCodeControl.updateValueAndValidity();
            this.onPostalCodeEntered(newPostalCode);
        });

        this.tripForm.valueChanges.subscribe(data => {
            // write the values to a temporary quote so other components can see them
            this.writeFormValues(this.temporaryQuote);
            this.temporaryQuoteChanged.emit(this.temporaryQuote);
            this.valuesChanged.emit(true);
        });
    }

    private initAdditionalTravelerForm(traveler: Traveler): FormGroup {
        return new FormGroup(
            {
                'dateOfBirthControl': new FormControl(traveler.birthDate || '', Validators.compose([Validators.required, dateOfBirthValidator()])),
                'tripCostControl': new FormControl(traveler.tripCost || '', Validators.compose([Validators.required, tripCostValidator(0, 100000)]))
            }
        );
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/quick-quote/quick-quote-entry.component.ts